import { useState } from "react";
import { ApiError, post, setCsrf, type Session } from "../api";

/**
 * One sign-in for the whole product.
 *
 * The demo accounts are listed because they are config, share one
 * well-known password, and exist to be signed into — and because not
 * listing them once sent somebody hunting for a username and typing in a
 * credential from a different prompt entirely. The password is not printed:
 * saying who exists is a convenience, printing it would make the sign-in
 * pointless. Against a real directory the server sends no accounts and
 * nothing is listed.
 */
export function Login({ onSignedIn, accounts, product }: {
  onSignedIn: (session: Session) => void;
  accounts: { subject: string; roles: string }[];
  product: string;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await post<Session>("/login", { username, password });
      setCsrf(session.csrf);
      onSignedIn(session);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main>
      <form className="login card" onSubmit={submit}>
        <h1 className="ser">{product}</h1>
        <p className="standfirst">
          Schedule-Impact &amp; Follow-Up Layer for NEC4. Sign in to continue.
        </p>
        <label htmlFor="u">Username</label>
        {/* No autoFocus: the browser scrolls the focused field into
            view, which pushed the product name off the top of a short
            window. The field is the first thing on the page anyway. */}
        <input id="u" autoComplete="username" required
          value={username} onChange={(e) => setUsername(e.target.value)} />
        <label htmlFor="p">Password</label>
        <input id="p" type="password" autoComplete="current-password" required
          value={password} onChange={(e) => setPassword(e.target.value)} />
        <button className="primary" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {error && <div className="err">{error}</div>}

        {accounts.length > 0 && (
          <div className="whoami">
            <div className="k">Demo accounts</div>
            <p className="hint">Each shows a different view of the same contract.</p>
            <table>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.subject}>
                    <td className="mn">{a.subject}</td>
                    <td className="withheld">{a.roles}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="hint">
              Local accounts standing in for the client's directory. They share one
              password — ask whoever sent you the link.
            </p>
          </div>
        )}
      </form>
    </main>
  );
}
